/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Literal, TranslateFunc } from '@steelbuy/ts-shared';
import { Dropdown } from '@steelbuy/ui-primitive';
import { SortComparator } from '@steelbuy/util';

type LabelMapper = (t: TranslateFunc) => string;

export interface ListingsFilterMaterialSelectionProps {
    name: any;
    options: any[];
    labelMapper?: (option: any, t: TranslateFunc) => string;
    fixedLabelMapper?: LabelMapper;
    valueMapper?: any;
    sort?: SortComparator;
}

export const ListingsFilterMaterialSelection = ({
    name,
    options,
    labelMapper,
    fixedLabelMapper,
    valueMapper,
    sort,
}: ListingsFilterMaterialSelectionProps) => {
    const { t } = useTranslation(['uiDomain', 'domainModel', 'translation']);
    const { watch, unregister, trigger, setValue, control } = useFormContext();
    const sortedOptions = sort ? options.sort(sort) : options;
    const selectOptions = sortedOptions.map((option: any) => ({
        label: (labelMapper?.(option, t as TranslateFunc) ??
            t(`domainModel:material.${name}.value.${option}` as Literal)) as string,
        value: valueMapper?.(option) ?? option,
    }));

    useEffect(() => {
        if (selectOptions.length) {
            const selectedValues: string[] = watch(name) || [];
            const validValues = selectOptions.map((opt) => opt.value);
            const filteredValues = selectedValues.filter((val) => validValues.includes(val));

            if (filteredValues.length !== selectedValues.length) {
                setValue(name, filteredValues.length > 0 ? filteredValues : undefined);
            }

            trigger(name);
        } else {
            unregister(name);
        }
    }, [JSON.stringify(selectOptions), trigger, name, unregister, watch]);

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange } }) => (
                <Dropdown
                    selectAll
                    multiselect
                    options={selectOptions}
                    onChange={(values) => {
                        if (!values.length) {
                            setValue(name, undefined, {
                                shouldDirty: true,
                                shouldValidate: true,
                                shouldTouch: true,
                            });
                            return;
                        }
                        onChange(values);
                    }}
                    preselection={watch(name)}
                    fixedLabel={
                        fixedLabelMapper?.(t as TranslateFunc) ?? t(`domainModel:material.${name}.name` as Literal)
                    }
                    disabled={options.length === 0}
                />
            )}
        />
    );
};
